import {useCallback, useContext, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import axios from 'axios';
import {Editor, EditorState, convertFromRaw} from 'draft-js';
import {mdToDraftjs} from 'draftjs-md-converter';
import {useTranslation} from 'react-i18next';
import {ContentPage} from '../../../../../components/content-page/content-page';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import {LoadingContent} from '../../../../../components/loading-content/loading-content';
import {AuthContext} from '../../../../../contexts/auth/context';
import {RoleOnRelationship} from '../../../../../backend/enums/role-on-relationship';
import {EntityServiceType} from '../../../../../backend/enums/entity-service-type';
import {EntityService} from '../../../../../backend/interfaces/entity-service';
import {AffiliationInfo} from '../../../../../backend/interfaces/affiliation-info';
import {ScreenPaths} from '../../../../../enums/screen-path';
import {translations} from '../../../../../i18n/translations';
import {UserEntityServicesRepository} from '../../../../../repositories/user-entity-services';
import {appColors} from '../../../../../utils/constants/colors';

const pathFor = (seniorId: string) =>
  `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${seniorId}${ScreenPaths.InformalCaregiver_Services}`;
type ErrorKind = 'error' | 'denied' | 'not_found';
const getError = (error: unknown): ErrorKind =>
  axios.isAxiosError(error) && error.response?.status === 403
    ? 'denied'
    : axios.isAxiosError(error) && error.response?.status === 404
      ? 'not_found'
      : 'error';

function Markdown({value}: {value: string}) {
  const state = useMemo(() => {
    try {
      return EditorState.createWithContent(convertFromRaw(mdToDraftjs(value)));
    } catch {
      return EditorState.createEmpty();
    }
  }, [value]);
  return <Editor editorState={state} onChange={() => {}} readOnly />;
}

export default function ServicesInformalCaregiverScreen({seniorId}: {seniorId: string}) {
  const {t} = useTranslation();
  const {entityId, serviceId} = useParams<{entityId?: string; serviceId?: string}>();
  const history = useHistory();
  const {state} = useContext(AuthContext);
  const relationship = state.session?.selectedRelationship;
  const affiliations = relationship?.withUser.affiliations || [];
  const eligible = affiliations.filter((a) => a.entityServicesAvailable === true);
  const [chosen, setChosen] = useState('');
  const [detailTitle, setDetailTitle] = useState<{id: string; title: string}>();
  useLayoutEffect(() => {
    if (entityId) setChosen(entityId);
  }, [entityId]);
  const entity = eligible.find(
    (a) =>
      a.entity.id === (entityId || chosen || (eligible.length === 1 ? eligible[0].entity.id : ''))
  );
  const base = pathFor(seniorId);
  return (
    <ContentPage fullWidth>
      <GenericHeader
        isLoading={false}
        pageTitle={serviceId ? undefined : t(translations.family_services.title)}
        titleCrumbs={
          serviceId
            ? [
                {label: t(translations.family_services.title), link: base},
                {
                  label: detailTitle?.id === serviceId ? detailTitle.title : '',
                  link: history.location.pathname,
                },
              ]
            : undefined
        }
        showBackButton={!!serviceId}
        leftColumn={<Box />}
      />
      <Stack spacing={3} sx={{mt: 3}}>
        {affiliations.length === 0 ? (
          <Typography color="text.secondary">{t(translations.family_services.none)}</Typography>
        ) : eligible.length === 0 ? (
          <Typography color="text.secondary">
            {t(translations.family_services.unavailable)}
          </Typography>
        ) : (
          <>
            {eligible.length > 1 && (
              <TextField
                select
                label={t(translations.family_services.choose)}
                value={entity?.entity.id || ''}
                onChange={(event) => {
                  setChosen(event.target.value);
                  history.push(base);
                }}
                sx={{maxWidth: 450}}
              >
                {eligible.map((a) => (
                  <MenuItem key={a.entity.id} value={a.entity.id}>
                    {a.entity.shortName || a.entity.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {entityId && !entity ? (
              <Alert severity="error">{t(translations.family_services.not_found)}</Alert>
            ) : entity ? (
              <ServiceCatalog
                key={`${seniorId}:${entity.entity.id}`}
                seniorId={seniorId}
                affiliation={entity}
                serviceId={serviceId}
                isAdmin={relationship?.role === RoleOnRelationship.Administrator}
                onDetailTitleChange={setDetailTitle}
              />
            ) : (
              <Typography color="text.secondary">
                {t(translations.family_services.choose)}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </ContentPage>
  );
}

function ServiceCatalog({
  seniorId,
  affiliation,
  serviceId,
  isAdmin,
  onDetailTitleChange,
}: {
  seniorId: string;
  affiliation: AffiliationInfo;
  serviceId?: string;
  isAdmin: boolean;
  onDetailTitleChange: (value: {id: string; title: string}) => void;
}) {
  const {t} = useTranslation();
  const history = useHistory();
  const {state} = useContext(AuthContext);
  const relationship = state.session?.selectedRelationship;
  const current = useRef({seniorId, isAdmin});
  current.current = {
    seniorId: relationship?.withUser.id || '',
    isAdmin: relationship?.role === RoleOnRelationship.Administrator,
  };
  const lifecycle = useRef({active: false});
  const pending = useRef(false);
  const [items, setItems] = useState<EntityService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorKind>();
  const [refreshError, setRefreshError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [confirm, setConfirm] = useState<EntityService>();
  const entityId = affiliation.entity.id;
  const base = pathFor(seniorId);
  const isCurrent = useCallback(
    (scope: {active: boolean}) => scope.active && current.current.seniorId === seniorId,
    [seniorId]
  );
  const load = useCallback(
    async (refresh = false) => {
      const scope = lifecycle.current;
      if (!refresh) {
        setLoading(true);
        setError(undefined);
      }
      setRefreshError(false);
      try {
        const result = await UserEntityServicesRepository.get(seniorId, entityId);
        if (isCurrent(scope))
          setItems(result.filter((item) => item.entityId === entityId && !!item.id));
      } catch (e) {
        if (isCurrent(scope)) {
          if (refresh) setRefreshError(true);
          else setError(getError(e));
        }
      } finally {
        if (isCurrent(scope)) setLoading(false);
      }
    },
    [seniorId, entityId, isCurrent]
  );
  useLayoutEffect(() => {
    const scope = {active: true};
    lifecycle.current = scope;
    void load();
    return () => {
      scope.active = false;
    };
  }, [load]);
  const toggle = async () => {
    if (
      !confirm ||
      !confirm.id ||
      pending.current ||
      !current.current.isAdmin ||
      current.current.seniorId !== seniorId ||
      confirm.entityId !== entityId
    )
      return;
    const target = confirm;
    const targetId = confirm.id;
    const scope = lifecycle.current;
    pending.current = true;
    setSaving(true);
    setSaveError(false);
    setSaved(false);
    try {
      await UserEntityServicesRepository.setActive(seniorId, targetId, !target.isActive);
      if (!isCurrent(scope)) return;
      setItems((previous) =>
        previous.map((item) =>
          item.id === target.id ? {...item, isActive: !target.isActive} : item
        )
      );
      setConfirm(undefined);
      setSaved(true);
      await load(true);
    } catch {
      if (isCurrent(scope)) setSaveError(true);
    } finally {
      if (isCurrent(scope)) {
        pending.current = false;
        setSaving(false);
      }
    }
  };
  const sorted = [...items].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive) || a.displayOrder - b.displayOrder
  );
  const service = serviceId ? items.find((item) => item.id === serviceId) : undefined;
  useLayoutEffect(() => {
    if (serviceId && service) onDetailTitleChange({id: serviceId, title: service.title});
  }, [serviceId, service, onDetailTitleChange]);
  const phoneFor = (value?: string) => {
    const cleaned = value?.replace(/[\s().-]/g, '') || '';
    return /^\+?\d{7,15}$/.test(cleaned) ? cleaned : '';
  };
  const phone = phoneFor(service?.phoneNumber) || phoneFor(affiliation.entity.servicesPhoneNumber);
  const supported =
    service?.type === EntityServiceType.CallGeneralNumber ||
    service?.type === EntityServiceType.CallSpecificNumber;
  return (
    <>
      {saved && <Alert severity="success">{t(translations.family_services.saved)}</Alert>}
      {refreshError && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" onClick={() => void load(true)}>
              {t(translations.family_services.retry)}
            </Button>
          }
        >
          {t(translations.family_services.refresh_error)}
        </Alert>
      )}
      {loading ? (
        <LoadingContent />
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => void load()}>
              {t(translations.family_services.retry)}
            </Button>
          }
        >
          {t(translations.family_services[error])}
        </Alert>
      ) : serviceId ? (
        service ? (
          <Card elevation={0} sx={{borderRadius: '10px'}}>
            <CardContent sx={{px: {xs: 2, md: 5, lg: 8}, py: 4}}>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={service.iconUrl || undefined}
                    sx={{
                      bgcolor: appColors.appButtons.entityServices.enabled,
                      width: 56,
                      height: 56,
                    }}
                  >
                    <BusinessCenterOutlinedIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5">{service.title}</Typography>
                    {isAdmin && (
                      <Typography color="text.secondary">
                        {t(
                          service.isActive
                            ? translations.family_services.active
                            : translations.family_services.inactive
                        )}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Divider />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: service.imageUrl ? 'minmax(180px, 280px) minmax(0, 1fr)' : '1fr',
                    },
                    gap: 3,
                  }}
                >
                  {service.imageUrl && (
                    <Box
                      sx={{
                        height: 220,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        bgcolor: appColors.appButtons.entityServices.enabled,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={service.imageUrl}
                        alt=""
                        sx={{width: '100%', height: '100%', objectFit: 'contain'}}
                      />
                    </Box>
                  )}
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                        {t(translations.entity_services.edit.description_input)}
                      </Typography>
                      <Markdown value={service.markdownDescription || ''} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                        {t(translations.entity_services.edit.details_input)}
                      </Typography>
                      <Markdown value={service.markdownDetails || ''} />
                    </Box>
                    {service.attachedDocumentInformativeNote && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                          {t(translations.entity_services.edit.document_informative_note_input)}
                        </Typography>
                        <Typography>{service.attachedDocumentInformativeNote}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
                {supported && phone ? (
                  <Button variant="contained" href={`tel:${phone}`} sx={{alignSelf: 'flex-start'}}>
                    {t(translations.family_services.call)}
                  </Button>
                ) : (
                  <Typography color="text.secondary">
                    {t(
                      supported
                        ? translations.family_services.no_phone
                        : translations.family_services.unsupported
                    )}
                  </Typography>
                )}
                {isAdmin && (
                  <Button
                    variant="outlined"
                    sx={{alignSelf: 'flex-start'}}
                    disabled={saving}
                    onClick={() => setConfirm(service)}
                  >
                    {t(
                      service.isActive
                        ? translations.family_services.disable
                        : translations.family_services.enable
                    )}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Typography>{t(translations.family_services.not_found)}</Typography>
        )
      ) : sorted.length === 0 ? (
        <Typography color="text.secondary">{t(translations.family_services.empty)}</Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))',
            gap: 2,
          }}
        >
          {sorted.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardActionArea onClick={() => history.push(`${base}/${entityId}/${item.id}`)}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={item.iconUrl || undefined}
                      sx={{
                        bgcolor: item.isActive
                          ? appColors.appButtons.entityServices.enabled
                          : 'text.disabled',
                        width: 56,
                        height: 56,
                      }}
                    >
                      <BusinessCenterOutlinedIcon />
                    </Avatar>
                    <Box sx={{flex: 1, minWidth: 0}}>
                      <Typography variant="subtitle1" sx={{overflowWrap: 'anywhere'}}>
                        {item.title}
                      </Typography>
                      {isAdmin && (
                        <Typography
                          variant="body2"
                          color={
                            item.isActive
                              ? appColors.appButtons.entityServices.enabled
                              : 'text.secondary'
                          }
                        >
                          {t(
                            item.isActive
                              ? translations.family_services.active
                              : translations.family_services.inactive
                          )}
                        </Typography>
                      )}
                    </Box>
                    <ChevronRightIcon color="action" />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
      <Dialog
        open={!!confirm}
        onClose={() => {
          if (!saving) {
            setConfirm(undefined);
            setSaveError(false);
          }
        }}
      >
        <DialogTitle>
          {confirm &&
            t(
              confirm.isActive
                ? translations.family_services.disable
                : translations.family_services.enable
            )}
          : {confirm?.title}?
        </DialogTitle>
        <DialogContent>
          {saveError && (
            <Alert severity="error">{t(translations.family_services.save_error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setConfirm(undefined)}>
            {t(translations.family_services.cancel)}
          </Button>
          <Button disabled={saving} onClick={() => void toggle()}>
            {t(translations.family_services.confirm)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
